import * as React from 'react';
import { createRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import type { DataHealthTile, MetricEntry } from '@bits/shared/zod';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export const analyticsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/analytics',
  component: AnalyticsDashboardPage,
});

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatRupees(paise: number): string {
  return (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

function formatPct(ratio: number): string {
  return `${(ratio * 100).toFixed(2)}%`;
}

const TILE_TONE: Record<DataHealthTile['severity'], string> = {
  ok: 'border-emerald-500/40 bg-emerald-500/10',
  warn: 'border-amber-500/60 bg-amber-500/10',
  alert: 'border-red-500/60 bg-red-500/10',
};

function AnalyticsDashboardPage(): JSX.Element {
  const [month, setMonth] = React.useState(currentMonth());
  const dictQuery = useQuery({
    queryKey: ['metrics-dictionary'],
    queryFn: () => api.getMetricsDictionary(),
  });
  const healthQuery = useQuery({
    queryKey: ['analytics', 'data-health'],
    queryFn: () => api.getDataHealth(),
  });
  const pnlQuery = useQuery({
    queryKey: ['analytics', 'project-pnl'],
    queryFn: () => api.getProjectPnl(),
  });
  const dsoQuery = useQuery({
    queryKey: ['analytics', 'dso'],
    queryFn: () => api.getDso(),
  });
  const reconQuery = useQuery({
    queryKey: ['analytics', 'reconciliation-lag'],
    queryFn: () => api.getReconciliationLag(),
  });
  const ratioQuery = useQuery({
    queryKey: ['analytics', 'payroll-cost-ratio', month],
    queryFn: () => api.getPayrollCostRatio(month),
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
  const utilQuery = useQuery({
    queryKey: ['analytics', 'utilization', month],
    queryFn: () => api.getUtilization(month),
    enabled: /^\d{4}-\d{2}$/.test(month),
  });
  const budgetQuery = useQuery({
    queryKey: ['analytics', 'budget-variance'],
    queryFn: () => api.getBudgetVariance(),
  });

  const dictById = React.useMemo(() => {
    const m = new Map<string, MetricEntry>();
    for (const e of dictQuery.data ?? []) m.set(e.id, e);
    return m;
  }, [dictQuery.data]);

  const health = healthQuery.data;
  const pnl = pnlQuery.data;
  const dso = dsoQuery.data;
  const recon = reconQuery.data;
  const ratio = ratioQuery.data;
  const util = utilQuery.data;
  const budget = budgetQuery.data;

  return (
    <AppShell>
      <div className="flex flex-wrap items-start justify-between gap-4 lg:flex-nowrap">
        <div className="flex min-w-0 flex-col gap-2">
          <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
            Analytics
          </h1>
          <p className="text-base text-[hsl(var(--muted-foreground))]">
            Point-in-time metrics against live tables. Every card links to its metric-dictionary
            entry and drills through to source rows. Basis is labeled per metric.
          </p>
          <Link to="/analytics/economics" className="text-sm font-medium text-[hsl(var(--brand))] hover:underline">
            Lead economics, NPV/IRR, TCO & scorecards →
          </Link>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="m">Month for month-scoped metrics</Label>
          <Input
            id="m"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.currentTarget.value)}
            className="w-44"
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Data-health</CardTitle>
          <CardDescription>
            {health
              ? `${health.tiles.length} tiles as of ${health.asOf}`
              : 'Loading…'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {(health?.tiles ?? []).map((tile) => (
              <Link
                key={tile.id}
                to={tile.deepLink as never}
                className={`flex flex-col gap-1 rounded-xl border p-4 transition-colors hover:opacity-90 ${TILE_TONE[tile.severity]}`}
              >
                <div className="text-3xl font-semibold">{tile.count}</div>
                <div className="text-sm font-medium">{tile.label}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{tile.description}</div>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <MetricCard
          entry={dictById.get('project_pnl')}
          value={
            pnl
              ? `₹ ${formatRupees(pnl.totals.marginPaise)} margin (${pnl.rows.length} projects)`
              : null
          }
          detail={
            pnl
              ? `Revenue ₹${formatRupees(pnl.totals.revenuePaise)} · Payroll ₹${formatRupees(pnl.totals.payrollCostPaise)} · Expenses ₹${formatRupees(pnl.totals.expenseCostPaise)}`
              : 'Loading…'
          }
        />
        <MetricCard
          entry={dictById.get('budget_variance')}
          value={
            budget
              ? `${budget.rows.filter((r) => r.variancePaise > 0).length} project(s) over budget`
              : null
          }
          detail={
            budget
              ? `Cumulative variance ₹${formatRupees(budget.rows.reduce((s, r) => s + r.variancePaise, 0))}`
              : 'Loading…'
          }
        />
        <MetricCard
          entry={dictById.get('utilization')}
          value={util ? `${util.avgBillablePct.toFixed(1)}% avg billable` : null}
          detail={
            util
              ? `${util.rows.length} active people for ${util.month}`
              : 'Loading…'
          }
        />
        <MetricCard
          entry={dictById.get('dso')}
          value={dso?.avgDays !== null && dso?.avgDays !== undefined ? `${dso.avgDays} days` : 'N/A'}
          detail={dso ? `${dso.paidDocumentCount} paid document(s)` : 'Loading…'}
        />
        <MetricCard
          entry={dictById.get('reconciliation_lag')}
          value={
            recon?.avgDays !== null && recon?.avgDays !== undefined
              ? `${recon.avgDays} days`
              : 'N/A'
          }
          detail={recon ? `${recon.reconciledCount} reconciled row(s)` : 'Loading…'}
        />
        <MetricCard
          entry={dictById.get('payroll_cost_ratio')}
          value={
            ratio?.ratio !== null && ratio?.ratio !== undefined ? formatPct(ratio.ratio) : 'N/A'
          }
          detail={
            ratio
              ? `Payroll ₹${formatRupees(ratio.payrollCostPaise)} · Revenue ₹${formatRupees(ratio.revenuePaise)} · ${ratio.month}`
              : 'Loading…'
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Metrics dictionary</CardTitle>
          <CardDescription>
            Every KPI has an entry with formula + basis. Ships in-product per spec §7.7.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col divide-y divide-[hsl(var(--border))]">
            {(dictQuery.data ?? []).map((m) => (
              <li key={m.id} className="flex flex-col gap-1 py-3 text-sm">
                <div className="flex items-center gap-2 font-medium">
                  {m.name}
                  <Badge variant="muted">{m.basis}</Badge>
                </div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  <code>{m.formula}</code>
                </div>
                <div className="text-xs">{m.description}</div>
                {m.drillTo ? (
                  <div className="text-xs">
                    <Link to={m.drillTo as never} className="text-[hsl(var(--primary))] hover:underline">
                      Drill to source →
                    </Link>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </AppShell>
  );
}

function MetricCard({
  entry,
  value,
  detail,
}: {
  entry: MetricEntry | undefined;
  value: string | null;
  detail: string;
}): JSX.Element {
  if (!entry) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>—</CardTitle>
        </CardHeader>
      </Card>
    );
  }
  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle>{entry.name}</CardTitle>
          <Badge variant="muted">{entry.basis}</Badge>
        </div>
        <CardDescription>
          <code className="break-words">{entry.formula}</code>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold">{value ?? '—'}</div>
        <div className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{detail}</div>
        {entry.drillTo ? (
          <div className="mt-3 text-xs">
            <Link to={entry.drillTo as never} className="text-[hsl(var(--primary))] hover:underline">
              Drill to source →
            </Link>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
